/* ---------------------------------------------------------------------------
   Closer Look - pinned layout
   Owns the whole pinned interaction: reads the scroll position, advances the
   feature, and paints the progress bar, counter and rail.

   Performance notes, because this runs on every scroll frame:
   - ONE getBoundingClientRect per frame, and every DOM write happens after it.
     Reading a geometry property after a write forces a synchronous layout, and
     doing that inside a scroll handler is what makes a pinned section feel
     heavy. Pill positions are measured once and cached for the same reason.
   - Nothing is written unless the value actually changed.
   - The component's own pinned handler is stood down (see STUB) so the element
     is not measured twice per frame.
   - No work at all while the section is outside the viewport.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var mqMobile = window.matchMedia('(max-width: 767px)');
  var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* bindPin's closure reads self.pin at call time, so handing it an object
     that reports zero height makes it bail on its second line - before it
     measures anything - instead of duplicating this file's work. */
  var STUB = {
    getBoundingClientRect: function () { return { height: 0, top: 0, bottom: 0 }; }
  };

  function attach(root) {
    if (root.__clPinFx) return true;
    if (root.getAttribute('data-mlayout') !== 'pinned') return true;

    var inst = root.hpCloserLook;
    if (!inst || !inst.data) return false;               // not booted yet

    var pin = root.querySelector('[data-cl-pin]');
    var n = (inst.data.features || []).length;
    if (!pin || !n) return true;

    root.__clPinFx = true;
    inst.pin = STUB;

    var segs = [].slice.call(root.querySelectorAll('.hp-cl__prog-seg > i'));
    var counter = root.querySelector('[data-cl-prog-now]');
    var rail = root.querySelector('.hp-cl__pills');
    var mtext = root.querySelector('[data-cl-mtext]');
    var step = parseInt(root.getAttribute('data-pin-step'), 10) || 80;

    var lastP = -1;
    var lastI = -1;
    var lastPinned = '';
    var fill = [];
    var railW = 0;
    var pillBox = [];
    var visible = false;

    /* ---- caption swap -------------------------------------------------
       The class-toggle version needed a forced reflow (void offsetWidth) to
       restart the CSS animation, which is a synchronous layout in the middle
       of a scroll frame. The Web Animations API restarts on its own. */
    if (mtext) {
      var basePaint = Object.getPrototypeOf(inst).paintMText;
      inst.paintMText = function () {
        basePaint.call(this);
        if (mqReduced.matches || !mtext.animate) return;
        mtext.animate(
          [
            { opacity: 0, transform: 'translateY(7px)' },
            { opacity: 1, transform: 'none' }
          ],
          { duration: 240, easing: 'cubic-bezier(.2,.6,.2,1)' }
        );
      };
    }

    /* ---- measurement (read-only, never during a scroll frame) --------- */
    var measure = function () {
      if (!mqMobile.matches) { pin.style.minHeight = ''; return; }

      // If the CSS track height never landed - svh unsupported, or a theme
      // override - the frame cannot pin and nothing appears to happen at all.
      if (pin.offsetHeight < window.innerHeight * 1.2) {
        pin.style.minHeight = (n * step + 100) + 'vh';
      }

      if (!rail) return;
      railW = rail.clientWidth;
      pillBox = [];
      for (var i = 0; i < n; i++) {
        var p = rail.querySelector('.hp-cl__pill[data-feature="' + i + '"]');
        pillBox.push(p ? { left: p.offsetLeft, w: p.offsetWidth } : null);
      }
    };

    /* Horizontal only: scrolls the rail, never the page, so it cannot fight
       the vertical gesture driving the section. Uses cached geometry. */
    var railTo = function (i) {
      if (!rail) return;
      var b = pillBox[i];
      if (!b) return;
      var left = b.left - (railW - b.w) / 2;
      if (left < 0) left = 0;
      if (rail.scrollTo) {
        rail.scrollTo({ left: left, behavior: mqReduced.matches ? 'auto' : 'smooth' });
      } else {
        rail.scrollLeft = left;
      }
    };

    var paint = function () {
      if (!mqMobile.matches) {
        if (lastPinned !== '') { lastPinned = ''; root.removeAttribute('data-pinned'); }
        return;
      }

      /* ---------- read ---------- */
      var r = pin.getBoundingClientRect();
      var vh = window.innerHeight;
      var travel = r.height - vh;
      if (travel <= 0) return;

      var p = -r.top / travel;
      if (p < 0) p = 0;
      if (p > 1) p = 1;

      var i = Math.floor(p * n);
      if (i > n - 1) i = n - 1;

      var pinnedNow = (r.top <= 1 && r.bottom >= vh - 1) ? 'true' : 'false';

      /* ---------- write ---------- */
      if (p !== lastP && (p === 0 || p === 1 || Math.abs(p - lastP) > 0.002)) {
        lastP = p;
        root.style.setProperty('--cl-pin-p', p.toFixed(4));

        for (var s = 0; s < segs.length; s++) {
          var f = p * n - s;
          if (f < 0) f = 0;
          if (f > 1) f = 1;
          // segments already full or still empty are the common case; skipping
          // them keeps this to one or two style writes per frame
          if (fill[s] === undefined || Math.abs(f - fill[s]) > 0.004) {
            fill[s] = f;
            segs[s].style.setProperty('--fill', f.toFixed(3));
          }
        }
      }

      if (pinnedNow !== lastPinned) {
        lastPinned = pinnedNow;
        root.setAttribute('data-pinned', pinnedNow);
      }

      if (i !== lastI) {
        lastI = i;
        if (counter) counter.textContent = (i + 1 < 10 ? '0' : '') + (i + 1);
        inst.select(i, false);
        railTo(i);
      }
    };

    var queued = false;
    var onScroll = function () {
      if (!visible || queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; paint(); });
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    var onResize = function () {
      lastP = -1; lastI = -1; fill = [];
      measure();
      paint();
    };
    window.addEventListener('resize', onResize);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onResize);

    // Do nothing while the section is off screen. A page with this section
    // near the footer should cost zero per scroll frame at the top.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) paint();
      }, { rootMargin: '150px 0px' }).observe(pin);
    } else {
      visible = true;
    }

    measure();
    paint();
    return true;
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('.hp-cl[data-mlayout="pinned"]');
    var all = true;
    [].slice.call(nodes).forEach(function (el) { if (!attach(el)) all = false; });
    return all;
  }

  /* closer-look.js constructs the component on DOMContentLoaded and this file
     is tagged after it, so its listener runs first. A themed page can reorder
     that, so poll briefly - and stop the moment everything is wired. */
  function boot(scope) {
    if (scan(scope)) return;
    var tries = 0;
    var t = window.setInterval(function () {
      tries++;
      if (scan(scope) || tries > 20) window.clearInterval(t);
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
